import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useRegistrarTratativa } from '../../shared/api/queries.ts';
import {
  DESFECHOS,
  DESFECHOS_DESCRITOS,
  type Desfecho,
  type GrupoDePendencia,
} from '../../shared/api/tipos.ts';
import { formatarDataSimples, formatarInteiro } from '../../shared/format.ts';

/**
 * Registra o que o escritório fez com um grupo de pendências.
 *
 * O desfecho vale para o grupo inteiro de um código — é assim que a rejeição
 * repetida trezentas vezes numa tarde se resolve de uma vez —, e o diálogo
 * mostra quantas notas são antes de qualquer coisa acontecer. A exceção é a
 * reemissão: a chave substituta é de **uma** nota, e aplicá-la a um grupo
 * diria que trezentas notas diferentes foram substituídas pela mesma.
 *
 * Nada aqui muda a situação da nota. Quem diz que ela está autorizada é a
 * SEFAZ; isto é o registro de uma decisão de quem atende a loja.
 */
export function DialogoDeDesfecho({
  aberto,
  grupo,
  empresaId,
  razaoSocial,
  periodo,
  aoFechar,
}: {
  aberto: boolean;
  /** `null` fecha o diálogo; é o grupo escolhido na triagem. */
  grupo: GrupoDePendencia | null;
  empresaId: string;
  razaoSocial: string;
  periodo: { de: string; ate: string };
  aoFechar: () => void;
}) {
  const [desfecho, setDesfecho] = useState<Desfecho>('em_cobranca');
  const [observacao, setObservacao] = useState('');
  const [chaveSubstituta, setChaveSubstituta] = useState('');
  const registrar = useRegistrarTratativa();

  const quantidade = grupo?.quantidade ?? 0;
  // A reemissão é de uma nota por vez, e o servidor recusa o grupo: dizer
  // isso aqui evita o erro depois de preencher o formulário inteiro.
  const grupoGrandeDemais = desfecho === 'reemitida' && quantidade > 1;

  function fechar() {
    registrar.reset();
    setDesfecho('em_cobranca');
    setObservacao('');
    setChaveSubstituta('');
    aoFechar();
  }

  async function confirmar() {
    if (grupo === null) return;

    await registrar.mutateAsync({
      empresaId,
      de: periodo.de === '' ? undefined : periodo.de,
      ate: periodo.ate === '' ? undefined : periodo.ate,
      codigoSefaz: grupo.codigo ?? undefined,
      desfecho,
      observacao: observacao.trim() === '' ? undefined : observacao.trim(),
      chaveSubstituta: chaveSubstituta.trim() === '' ? undefined : chaveSubstituta.trim(),
    });
    fechar();
  }

  return (
    <Dialog open={aberto && grupo !== null} onClose={fechar} fullWidth maxWidth="sm">
      <DialogTitle>Registrar desfecho</DialogTitle>

      <DialogContent>
        <DialogContentText component="div" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{razaoSocial}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {grupo?.codigo === null || grupo?.codigo === undefined
              ? 'Notas sem código da SEFAZ'
              : `Código ${grupo.codigo} — ${grupo.mensagem ?? ''}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatarInteiro(quantidade)} nota(s) no filtro em vigor
            {periodo.de === '' && periodo.ate === ''
              ? ', em todo o acervo da loja'
              : `, de ${periodo.de === '' ? '—' : formatarDataSimples(periodo.de)} a ${
                  periodo.ate === '' ? '—' : formatarDataSimples(periodo.ate)
                }`}
            .
          </Typography>
        </DialogContentText>

        <Stack spacing={2}>
          <TextField
            select
            label="O que houve com estas notas"
            value={desfecho}
            onChange={(evento) => setDesfecho(evento.target.value as Desfecho)}
            helperText={DESFECHOS_DESCRITOS[desfecho].ajuda}
            fullWidth
          >
            {DESFECHOS.map((valor) => (
              <MenuItem key={valor} value={valor}>
                {DESFECHOS_DESCRITOS[valor].rotulo}
              </MenuItem>
            ))}
          </TextField>

          {desfecho === 'reemitida' && (
            <TextField
              label="Chave da nota que ficou no lugar"
              value={chaveSubstituta}
              onChange={(evento) => setChaveSubstituta(evento.target.value)}
              slotProps={{ htmlInput: { maxLength: 60 } }}
              helperText="Sem ela, o vão na numeração continua sem resposta."
              fullWidth
            />
          )}

          <TextField
            label={desfecho === 'descartada' ? 'Por que está sendo descartada' : 'Observação'}
            value={observacao}
            onChange={(evento) => setObservacao(evento.target.value)}
            slotProps={{ htmlInput: { maxLength: 300 } }}
            multiline
            minRows={2}
            required={desfecho === 'descartada'}
            helperText="Fica no registro da nota — é a resposta de meses depois."
            fullWidth
          />

          {grupoGrandeDemais && (
            <Alert severity="warning">
              A reemissão se registra uma nota por vez: a chave substituta é de uma só. Abra a
              lista abaixo e registre pela linha da nota.
            </Alert>
          )}

          {registrar.isError && (
            <Alert severity="error">
              {registrar.error instanceof Error
                ? registrar.error.message
                : 'Não foi possível registrar o desfecho.'}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={fechar}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={() => void confirmar()}
          loading={registrar.isPending}
          disabled={grupoGrandeDemais}
        >
          Registrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
